/**
 * WebSocketContext - Real-time push notifications from the server.
 * Handles reconnection, visibility changes, and background tab throttling.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

type EventType = 
  | 'ticket:created' | 'ticket:updated' | 'ticket:deleted' | 'ticket:assigned' | 'ticket:status'
  | 'schedule:updated' | 'tracking:location' | 'tracking:clock'
  | 'user:updated' | 'customer:updated' | 'refresh:all';

type Listener = (data: any) => void;

interface WSContextType {
  connected: boolean;
  subscribe: (event: EventType | EventType[], callback: Listener) => () => void;
  clientCount: number;
}

const WSContext = createContext<WSContextType>({
  connected: false,
  subscribe: () => () => {},
  clientCount: 0,
});

export function useWebSocket() {
  return useContext(WSContext);
}

/**
 * Hook: subscribe to specific WS events and auto-refresh data.
 * Usage: useWSRefresh(['ticket:updated', 'ticket:assigned'], fetchMyData);
 */
export function useWSRefresh(events: EventType | EventType[], callback: (data?: any) => void) {
  const { subscribe } = useWebSocket();
  const callbackRef = useRef(callback);
  useEffect(() => { callbackRef.current = callback; });
  useEffect(() => {
    return subscribe(events, (data) => callbackRef.current(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe]);
}

/**
 * Hook: triggers callback when tab becomes visible (un-throttles polling).
 */
export function useVisibilityRefresh(callback: () => void) {
  const callbackRef = useRef(callback);
  useEffect(() => { callbackRef.current = callback; });
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        callbackRef.current();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);
}

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const wsRef = useRef<WebSocket | null>(null);
  const listenersRef = useRef<Map<EventType, Set<Listener>>>(new Map());
  const [connected, setConnected] = useState(false);
  const [clientCount] = useState(0);
  const reconnectTimer = useRef<any>(null);
  const pingTimer = useRef<any>(null);
  const reconnectDelay = useRef(1000);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) return;

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${proto}//${window.location.host}/ws`;
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        setConnected(true);
        reconnectDelay.current = 1000; // Reset backoff
        console.log('[WS] Connected');
        // Start ping every 25s to keep alive
        pingTimer.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 25000);
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'pong') return;
          
          // Dispatch to listeners
          const eventType = msg.type as EventType;
          const listeners = listenersRef.current.get(eventType);
          if (listeners) {
            listeners.forEach(cb => {
              try { cb(msg.data); } catch (e) { console.error('[WS] Listener error:', e); }
            });
          }
          
          // Also dispatch refresh:all for any ticket/schedule/tracking event
          if (eventType.startsWith('ticket:') || eventType.startsWith('schedule:') || eventType.startsWith('tracking:')) {
            const allListeners = listenersRef.current.get('refresh:all');
            if (allListeners) {
              allListeners.forEach(cb => {
                try { cb(msg.data); } catch (e) {}
              });
            }
          }
        } catch {}
      };

      ws.onclose = () => {
        setConnected(false);
        clearInterval(pingTimer.current);
        const delay = Math.min(reconnectDelay.current, 10000);
        console.log(`[WS] Disconnected, reconnecting in ${delay}ms...`);
        reconnectTimer.current = setTimeout(connect, delay);
        reconnectDelay.current = Math.min(reconnectDelay.current * 1.5, 10000);
      };

      ws.onerror = () => {
        ws.close();
      };

      wsRef.current = ws;
    } catch {
      reconnectTimer.current = setTimeout(connect, 5000);
    }
  }, []);

  useEffect(() => {
    connect();

    // Reconnect immediately when tab becomes visible
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          clearTimeout(reconnectTimer.current);
          connect();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearTimeout(reconnectTimer.current);
      clearInterval(pingTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const subscribe = useCallback((events: EventType | EventType[], callback: Listener) => {
    const evtList = Array.isArray(events) ? events : [events];
    
    evtList.forEach(evt => {
      if (!listenersRef.current.has(evt)) {
        listenersRef.current.set(evt, new Set());
      }
      listenersRef.current.get(evt)!.add(callback);
    });

    // Return unsubscribe function
    return () => {
      evtList.forEach(evt => {
        listenersRef.current.get(evt)?.delete(callback);
      });
    };
  }, []);

  return (
    <WSContext.Provider value={{ connected, subscribe, clientCount }}>
      {children}
    </WSContext.Provider>
  );
}
